<template>
  <div class="chat-widget">
    <!-- 聊天按钮 -->
    <button 
      v-if="!isChatOpen" 
      @click="openChat" 
      class="chat-button"
    >
      <span class="chat-icon">💬</span>
    </button>

    <!-- 聊天对话框 -->
    <div 
      v-if="isChatOpen" 
      class="chat-container"
    >
      <div class="chat-header">
        <div class="header-title">
          <div class="avatar">AI</div>
          <h2>AI 助手</h2>
        </div>
        <div class="header-actions">
          <button class="close-button" @click="closeChat">✕</button>
        </div>
      </div>
      
      <div class="chat-messages" ref="messagesContainer">
        <div v-for="(message, index) in chatHistory" :key="index" 
             :class="['message', message.isUser ? 'user-message' : 'ai-message']">
          <div class="message-bubble">
            <p>{{ message.text }}</p>
            <div v-if="message.links && message.links.length > 0" class="message-links">
              <div v-for="(link, linkIndex) in message.links" :key="linkIndex" class="link-item">
                {{ link }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="isTyping" class="message ai-message">
          <div class="message-bubble">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="chat-input">
        <input 
          type="text" 
          v-model="newMessage" 
          placeholder="输入您的问题..." 
          @keyup.enter="sendMessage"
        />
        <button class="send-button" @click="sendMessage">
          <span class="send-icon">➤</span>
        </button>
      </div>

      <!-- 快捷问题按钮 -->
      <div class="quick-questions">
        <p class="quick-title">您可以尝试以下问题：</p>
        <div class="questions-container">
          <button 
            v-for="(question, index) in suggestedQuestions" 
            :key="index"
            @click="askQuestion(question)"
            class="question-button"
          >
            {{ question }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';

const isChatOpen = ref(false);
const newMessage = ref('');
const chatHistory = ref([]);
const isTyping = ref(false);
const messagesContainer = ref(null);

// 预定义的问答对
const qaDatabase = [
{
    question: "云岗石窟有什么值得打卡的",
    answer: {
      text: "云冈石窟是中国三大石窟之一，以下是值得打卡的亮点：",
      links: [
        "1. 第20窟露天大佛：云冈石窟的标志性佛像，高13.7米，气势恢宏",
        "2. 音乐窟（第12窟）：窟内雕刻有大量古代乐器形象，展现北魏音乐文化",
        "3. 五华洞（第9-13窟）：色彩保存最完好的洞窟，展现1500年前的绚丽彩绘",
        "4. 昙曜五窟（第16-20窟）：最早开凿的五个洞窟，代表北魏佛教艺术巅峰",
        "5. 石窟博物馆：全面展示云冈石窟的历史、艺术和保护成果"
      ]
    }
  },
  {
    question: "山西的传统文化都有什么",
    answer: {
      text: "山西拥有丰富多彩的传统文化，主要包括：",
      links: [
        "1. 晋商文化：以乔家大院、王家大院为代表的晋商建筑和商业文化",
        "2. 佛教文化：五台山佛教圣地、云冈石窟等佛教艺术瑰宝",
        "3. 民间艺术：剪纸、面塑、皮影等非物质文化遗产",
        "4. 戏曲文化：晋剧、北路梆子等地方戏曲剧种",
        "5. 饮食文化：刀削面、老陈醋、平遥牛肉等特色美食",
        "6. 民俗文化：社火、秧歌等传统民俗活动"
      ]
    }
  },
  {
    question: "你是谁",
    answer: {
      text: "我是一个预设回答的AI助手演示程序。我只能回答几个固定的问题，包括关于山西景点、学习编程的理由和人工智能发展历程等。您可以尝试点击下方的快捷问题按钮来与我交流。",
      links: []
    }
  },
  {
    question: "你能做什么",
    answer: {
      text: "我是一个简单的演示程序，只能回答几个预设的问题。我可以告诉您：",
      links: [
        "- 山西的热门旅游景点",
        "- 云岗石窟有什么值得打卡的",
        "- 山西的传统文化都有什么",
        "您可以尝试点击下方的快捷问题按钮来查看我的回答。"
      ]
    }
  }
];

// 快捷问题列表
const suggestedQuestions = [
  "请推荐一下山西的热门景点",
  "云岗石窟有什么值得打卡的",
  "山西的传统文化都有什么",
  "你是谁",
  "你能做什么"
];

// 初始欢迎消息
function showWelcomeMessage() {
  chatHistory.value = [{
    isUser: false,
    text: "您好！我是AI助手，请问有什么可以帮助您的吗？",
    links: []
  }];
}

// 打开聊天窗口
function openChat() {
  isChatOpen.value = true;
  if (chatHistory.value.length === 0) {
    showWelcomeMessage();
  }
}

// 关闭聊天窗口
function closeChat() {
  isChatOpen.value = false;
}

// 发送消息
function sendMessage() {
  if (newMessage.value.trim() === '') return;
  
  // 添加用户消息到聊天历史
  chatHistory.value.push({
    isUser: true,
    text: newMessage.value
  });
  
  // 清空输入框
  const userQuestion = newMessage.value;
  newMessage.value = '';
  
  // 显示"正在输入"指示器
  isTyping.value = true;
  
  // 模拟AI思考时间
  setTimeout(() => {
    // 查找匹配的问题
    const answer = findAnswer(userQuestion);
    isTyping.value = false;
    
    // 添加AI回复
    chatHistory.value.push({
      isUser: false,
      text: answer.text,
      links: answer.links
    });
    
    // 滚动到底部
    scrollToBottom();
  }, 1000);
}

// 通过快捷按钮提问
function askQuestion(question) {
  newMessage.value = question;
  sendMessage();
}

// 查找问题的答案
function findAnswer(question) {
  // 简化问题文本以便匹配
  const simplifiedQuestion = question.toLowerCase().trim().replace(/[?？!！。.,，]/g, '');
  
  // 尝试找到匹配的问题
  for (const qa of qaDatabase) {
    const dbQuestion = qa.question.toLowerCase().trim().replace(/[?？!！。.,，]/g, '');
    
    // 如果问题完全匹配或包含关键词
    if (simplifiedQuestion === dbQuestion || 
        simplifiedQuestion.includes(dbQuestion) || 
        dbQuestion.includes(simplifiedQuestion)) {
      return qa.answer;
    }
  }
  
  // 如果没有找到匹配的问题，返回默认回答
  return {
    text: "抱歉，我只能回答几个预设的问题。您可以尝试点击下方的快捷问题按钮，或者询问我'你能做什么'来了解我可以回答的问题。",
    links: []
  };
}

// 监听聊天历史变化，自动滚动到底部
watch(chatHistory, () => {
  scrollToBottom();
}, { deep: true });

// 滚动到聊天窗口底部
function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}
</script>

<style scoped>
/* 基础样式 */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

/* 聊天按钮样式 */
.chat-button {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #10b981;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
  z-index: 1000;
}

.chat-button:hover {
  background-color: #059669;
  transform: scale(1.05);
}

.chat-icon {
  font-size: 24px;
}

/* 聊天容器样式 */
.chat-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 350px;
  max-width: 90vw;
  background-color: #f9fafb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  animation: pop-in 0.3s ease-out;
  transform-origin: bottom right;
}

/* 聊天头部样式 */
.chat-header {
  background-color: white;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
}

.header-title {
  display: flex;
  align-items: center;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #10b981;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.header-title h2 {
  margin-left: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.close-button {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
}

.close-button:hover {
  color: #4b5563;
}

/* 聊天消息区域样式 */
.chat-messages {
  height: 320px;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.user-message {
  justify-content: flex-end;
}

.ai-message {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 80%;
  padding: 12px;
  border-radius: 18px;
}

.user-message .message-bubble {
  background-color: #10b981;
  color: white;
  border-top-right-radius: 4px;
}

.ai-message .message-bubble {
  background-color: white;
  color: #1f2937;
  border-top-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-bubble p {
  font-size: 14px;
  line-height: 1.5;
}

.message-links {
  margin-top: 8px;
}

.link-item {
  font-size: 12px;
  margin-top: 4px;
}

/* 输入区域样式 */
.chat-input {
  display: flex;
  padding: 12px;
  background-color: white;
  border-top: 1px solid #e5e7eb;
}

.chat-input input {
  flex-grow: 1;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  padding: 8px 16px;
  font-size: 14px;
  outline: none;
}

.chat-input input:focus {
  border-color: #10b981;
}

.send-button {
  margin-left: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #10b981;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.send-button:hover {
  background-color: #059669;
}

.send-icon {
  font-size: 12px;
  transform: rotate(90deg);
}

/* 快捷问题区域样式 */
.quick-questions {
  padding: 12px;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.quick-title {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.questions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.question-button {
  font-size: 12px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 4px 12px;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.2s;
}

.question-button:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

/* 打字指示器样式 */
.typing-indicator {
  display: flex;
  align-items: center;
}

.typing-indicator span {
  height: 8px;
  width: 8px;
  margin: 0 1px;
  background-color: #bbbbbb;
  border-radius: 50%;
  display: inline-block;
  animation: typing 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

/* 滚动条样式 */
.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #dddddd;
  border-radius: 10px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #cccccc;
}

/* 动画效果 */
@keyframes pop-in {
  0% {
    transform: scale(0.95);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes typing {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
}

/* 响应式调整 */
@media (max-width: 480px) {
  .chat-container {
    width: 90vw;
    bottom: 16px;
    right: 16px;
  }
  
  .chat-button {
    bottom: 16px;
    right: 16px;
    width: 50px;
    height: 50px;
  }
  
  .chat-messages {
    height: 280px;
  }
}
</style>